import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useRouter } from 'next/router'
import PropTypes from 'prop-types'
import SendbirdChat from '@sendbird/chat'
import { GroupChannelHandler } from '@sendbird/chat/groupChannel'
import log from 'sentry/log'
import {
  setRemovedChannelUrl,
  errorHandler as chatErrorHandler,
  fetchConnectProperties,
  setUserSuspendedType,
} from 'reduxBits/chat/actions'
import { setCarouselImages } from 'reduxBits/ui/actions'
import { actionCreators as api } from 'reduxBits/api'
import {
  trackingUserBlocksUser,
  trackingUserUnBlocksUser,
  userViewsChatConversation,
  trackingUserSendMessageError,
  userSentChatMessage,
  tracking3dPartyAdLoaded,
  trackingWarningModalAction,
  trackModalView,
  trackReportUserInvalidDescription,
  trackCustomerServiceLink,
  trackingUserSentFile,
} from 'tracking/chat'
import { formatBlockReasonTracking } from 'tracking/chat/utils'
import { sessionStorageGetItem, sessionStorageRemoveItem } from 'utils/useSessionStorage'
import { convertBadgesToJsx } from 'utils/convertBadgesToJsx'
import { SMS_AND_BANKID, BANKID } from 'constants/verificationTypes'
import { Container } from './style.js'
import ChannelView from './ChannelView'
import ChannelAdInfo from './ChannelAdInfo'
import ActionDropDown from './ActionDropDown'
import RemoveChannelModal from './RemoveChannelModal'
import InputField from './InputField'
import VerificationPrompt from './VerificationPrompt'
import ChannelPrompt from './ChannelPrompt'
import PreviewImages from './PreviewImages'
import { MESSAGE_BLOCK_REASONS } from '../../constants/messageBlockReasons'
import { JWT_ERROR_TYPES } from '../../constants/jwtErrorTypes'
import { allSettled } from '../../utils/polyfills'
import { validateFiles } from '../../functions/validateFiles'
import { formatErrorFileUploading } from '../../functions/formatErrorFileUploading'
import useClickOutsideElement from '../../customHooks/useClickOutsideElement'
import { useOnlineStatus } from '../../customHooks/useOnlineStatus'
import { useInnerHeight } from '../../customHooks/useInnerHeight'
import useFocus from '../../customHooks/useFocus'
import useChannelInfoData from 'components/pagesContent/Chat/customHooks/useChannelInfoData'
import { scrollToBottom, scrollToSavedPos } from '../../utils/events/scroll'
import { validateChannel } from '../../utils/validateChannel'
import getMembersFromChannelData from '../../utils/getMembersFromChannelData'
import { getMsgParams, getImgParams, getFileParams } from '../../utils/sendbirdParams'
import formatMessageLinks from '../../utils/fraudPrevention/formatMessageLinks'
import checkExternalContacts from '../../utils/fraudPrevention/checkExternalContacs'
import { reportUser } from '../../utils/sendbird/channel/report'
import WarningModalContent from 'components/sharedComponents/Modals/WarningModal'
import { WarningModal } from 'components/sharedComponents/Modals/WarningModal/styles'
import ReportUserModal from 'components/sharedComponents/Modals/ReportUserModal'
import { localStorageSetItem, localStorageGetItem } from 'utils/useLocalStorage'

const LoadedChannel = ({ isMobile, selectedChannel, currentUserId, pathnameId, isAdReplyView }) => {
  const sb = SendbirdChat.instance
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [typing, setTyping] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [uploadingInChannelUrl, setUploadingInChannelUrl] = useState('')
  const [recipientReadLastMessage, setRecipientReadLastMessage] = useState(false)
  const [showActionDropDown, setShowActionDropDown] = useState(false)
  const [showRemoveChannelModal, setShowRemoveChannelModal] = useState(false)
  const [recipientIsBlocked, setRecipientIsBlocked] = useState(false)
  const [recipient, setRecipient] = useState({})
  const [recipientName, setRecipientName] = useState('')
  const [role, setRole] = useState('')
  const [extensionData, setExtensionData] = useState(null)
  const [updateExtensionData, setUpdateExtensionData] = useState(true)
  const [tip, setTip] = useState(null)
  const [messageListQuery, setMessageListQuery] = useState()
  const [savedScrollPos, setSavedScrollPos] = useState()
  const [queryState, setQueryState] = useState({
    channelId: '',
    page: 0,
  })
  const [needChannelUpdate, setNeedChannelUpdate] = useState(false)
  const [warningModal, setWarningModal] = useState({})
  const [reportUserModal, setReportUserModal] = useState({
    activePage: 'form',
    open: false,
    formValidation: {
      status: '',
      text: '',
    },
  })
  const [reportData, setReportData] = useState({
    offendingUser: selectedChannel?.members?.find((member) => member.userId !== currentUserId),
    selectedOption: {
      label: '',
      reportCategory: '',
      trackingCategory: '',
      descriptionRequired: null,
    },
    description: '',
  })
  const [previewImages, setPreviewImages] = useState([])
  const [profileData, setProfileData] = useState({})

  const { connected } = useSelector((state) => state.chat.sendbirdUser)
  const warningModalType = useSelector((state) => state.chat.warningModal?.type)
  const { uriModeration, blockReasons } = useSelector((state) => state.chat.clientSettings)
  const { type } = useSelector((state) => state.account.verification)
  const { mcSendEmailCopy } = useSelector((state) => state.account.user.propertyMap)
  const {
    noSilentBlock: noSilentBlockFeature,
    suspendedAccount: suspendedAccountFeature,
  } = useSelector((state) => state.optimizely.features)

  const userOnline = useOnlineStatus()
  const dispatch = useDispatch()
  const listRef = useRef()
  const modalRef = useRef()
  const imageUploaderRef = useRef()
  const fileUploaderRef = useRef()
  const router = useRouter()
  const sessionStorageKey = `chat:message:channel=${pathnameId}:user=${currentUserId}`
  const innerHeight = useInnerHeight()
  const [textInputRef, focusTextInputRef] = useFocus()
  const needsVerification = type === BANKID || type === SMS_AND_BANKID
  const isFirstMessage = messages.length === 0

  const { recipientIsBankIdVerified, advertiserType } = useChannelInfoData({
    selectedChannel,
    recipient,
  })

  // prevent "action dropdown" toggle if report user modal is open
  useClickOutsideElement(modalRef, () => {
    if (!reportUserModal.open) {
      setShowActionDropDown(false)
    }
  })

  const recipientBlocketAccountId = getMembersFromChannelData(selectedChannel)?.find(
    (m) => m?.sendbirdAccountId === recipient?.userId,
  )?.blocketAccountId

  const getVerificationToken = useCallback(
    async (forceFetch = false) => {
      const { verificationToken } = (await dispatch(fetchConnectProperties(forceFetch))) || {}
      return JSON.stringify({ verification_token: verificationToken })
    },
    [dispatch],
  )

  const removePreviewImage = useCallback(
    (imageId) => {
      setPreviewImages((prevPreviewImages) =>
        prevPreviewImages.filter((previewImage) => previewImage?.id !== imageId),
      )
    },
    [setPreviewImages],
  )

  const resetReportUserFormValidation = useCallback(() => {
    setReportUserModal({
      ...reportUserModal,
      formValidation: {
        ...reportUserModal.formValidation,
        status: '',
        text: '',
      },
    })
  }, [reportUserModal])

  const focusInputField = useCallback(() => {
    // setTimeout solution to await the modal disappearing from DOM
    setTimeout(() => focusTextInputRef(), 10)
  }, [focusTextInputRef])

  const onWarningModalClose = useCallback(() => {
    setWarningModal({ ...warningModal, open: false })
    focusInputField()
  }, [focusInputField, warningModal])

  const formatSingleMessage = useCallback(
    (unformattedMessage) => {
      const isRecipientMessage = unformattedMessage?.sender?.userId !== currentUserId
      if (unformattedMessage.customType !== 'V1.TXT') {
        return unformattedMessage
      }
      return formatMessageLinks(
        unformattedMessage,
        warningModal,
        setWarningModal,
        isRecipientMessage,
        selectedChannel.url,
        !uriModeration,
      )
    },
    [currentUserId, warningModal, selectedChannel.url, uriModeration],
  )

  const formatMessages = useCallback(
    (unformattedMessages) =>
      unformattedMessages.map((unformattedMessage) => {
        const isRecipientMessage = unformattedMessage?.sender?.userId !== currentUserId
        if (unformattedMessage.customType !== 'V1.TXT') {
          return unformattedMessage
        }
        return formatMessageLinks(
          unformattedMessage,
          warningModal,
          setWarningModal,
          isRecipientMessage,
          selectedChannel.url,
          !uriModeration,
        )
      }),
    [currentUserId, warningModal, selectedChannel.url, uriModeration],
  )

  const setChatError = useCallback(
    ({ error, errorType, sentryLog }) => {
      if (!error) return
      dispatch(chatErrorHandler({ error, errorType, sentryLog }))
    },
    [dispatch],
  )

  const addChannelHandler = useCallback(() => {
    const inSelectedChannel = (channel, cb) => {
      if (channel.url === selectedChannel.url) {
        cb()
      }
    }
    const loadedGroupChannelHandler = new GroupChannelHandler({
      onMetaDataUpdated: (channel, metaDataMap) => {
        inSelectedChannel(channel, () => {
          if (extensionData?.transactionStateId !== metaDataMap?.transaction_state_id) {
            setUpdateExtensionData(true)
          }
        })
      },
      onMessageReceived: (channel, newMessage) => {
        const formattedMessage = formatSingleMessage(newMessage)
        inSelectedChannel(channel, () => {
          if (newMessage.customType === 'V1.EXTENSION') {
            setUpdateExtensionData(true)
          }
          setMessages((prevMessages) => [...prevMessages, formattedMessage])
          scrollToBottom(listRef.current)
        })
      },
      onTypingStatusUpdated: (channel) => {
        inSelectedChannel(channel, () => {
          setTyping(channel.isTyping)
        })
      },
      onUnreadMemberStatusUpdated: (channel) => {
        inSelectedChannel(channel, () => {
          if (selectedChannel.getReadStatus(channel.lastMessage) <= 0) {
            setRecipientReadLastMessage(true)
          }
        })
      },
    })

    sb.groupChannel.addGroupChannelHandler(selectedChannel.url, loadedGroupChannelHandler)
  }, [sb, selectedChannel, extensionData?.transactionStateId, formatSingleMessage, messages])

  const setPrevMsgListQuery = useCallback(() => {
    if (selectedChannel?.custom) return

    const prevMsgListQuery = selectedChannel?.createPreviousMessageListQuery({ limit: 30 })
    setQueryState({
      channelId: selectedChannel.url,
      page: 0,
    })
    setMessageListQuery(prevMsgListQuery)
  }, [selectedChannel])

  const loadMessages = useCallback(async () => {
    if (!(messageListQuery?.hasNext && queryState.channelId === selectedChannel.url)) {
      return
    }

    try {
      const previousMessages = await messageListQuery.load()
      const formattedMessages = formatMessages(previousMessages)

      setMessages((currentMessages) => [...formattedMessages, ...currentMessages])
      setQueryState((previousState) => ({
        ...previousState,
        page: previousState.page + 1,
      }))
      scrollToBottom(listRef.current)
    } catch (error) {
      setChatError({
        error,
        errorType: 'general',
        sentryLog: {
          description: 'Chat: Sendbird load messages error',
        },
      })
    }
  }, [messageListQuery, queryState.channelId, selectedChannel.url, formatMessages, setChatError])

  const paginateMessages = useCallback(async () => {
    if (
      messageListQuery?.hasNext &&
      !messageListQuery?.isLoading &&
      listRef.current &&
      userOnline
    ) {
      setSavedScrollPos(listRef.current.scrollHeight)
      await loadMessages()
    }
  }, [messageListQuery, userOnline])

  const uploadImages = useCallback(async () => {
    setUploadingInChannelUrl(selectedChannel.url)
    const sendPreviewImages = previewImages.map((previewImage) => {
      return new Promise(async (resolve, reject) => {
        const sendImage = async (imageFile = previewImage, retry = 0, forceFetch = false) => {
          const maxRetries = 3
          selectedChannel
            .sendFileMessage(getImgParams(imageFile, await getVerificationToken(forceFetch)))
            .onSucceeded((fileMessage) => {
              if (selectedChannel.url === fileMessage.channelUrl) {
                setMessages((prevMessages) => [...prevMessages, fileMessage])
                removePreviewImage(imageFile.id)
                scrollToBottom(listRef.current)
              }
              trackingUserSentFile({ selectedChannel })
              resolve(fileMessage)
            })
            .onFailed((error) => {
              if (
                suspendedAccountFeature?.enabled &&
                error?.message === '400301_account_suspended'
              ) {
                log.message(
                  `SuspendedUser error: ${error}. Sendbird SDK method: sendFileMessage -> uploadImages.`,
                )
                return dispatch(setUserSuspendedType(error.message))
              }

              if (JWT_ERROR_TYPES.includes(error?.message) && retry <= maxRetries) {
                return sendImage(imageFile, retry + 1, true)
              }

              trackingUserSendMessageError({
                selectedChannel,
                error: error.data,
                type: 'image',
              })
              reject(error)
            })
        }
        await sendImage()
      })
    })

    if (await allSettled(sendPreviewImages)) {
      setUploadingInChannelUrl(null)
    }
  }, [previewImages, selectedChannel, suspendedAccountFeature?.enabled])

  const sendMessage = useCallback(
    async (e, retry = 0, forceFetch = false) => {
      const maxRetries = 3
      e.preventDefault()

      // Return early if a message is already being sent
      if (isSending) return

      setIsSending(true)

      if (previewImages?.length) {
        await uploadImages()
      }

      if (input) {
        selectedChannel
          .sendUserMessage(getMsgParams(input, await getVerificationToken(forceFetch)))
          .onSucceeded((newMessage) => {
            setIsSending(false)
            const formattedMessage = formatSingleMessage(newMessage)
            if (!mcSendEmailCopy && !localStorageGetItem('SendEmailCopyShown')) {
              setWarningModal({
                open: true,
                children: (
                  <WarningModalContent
                    type="send-email-copy"
                    closeModal={() => setWarningModal({ ...warningModal, open: false })}
                    viewTracking={() => {
                      trackModalView({
                        objectId: 'email_optin_modal',
                        eventName: 'Email optin modal',
                      })
                    }}
                  />
                ),
              })
              localStorageSetItem('SendEmailCopyShown', true)
            }
            userSentChatMessage({ selectedChannel, isFirstMessage })
            setMessages((prevMessages) => [...prevMessages, formattedMessage])
            sessionStorageRemoveItem(sessionStorageKey)
            setInput('')
            scrollToBottom(listRef.current)
          })
          .onFailed((error) => {
            setIsSending(false)
            trackingUserSendMessageError({ selectedChannel, error: error?.data, type: 'text' })

            if (JWT_ERROR_TYPES.includes(error?.message) && retry <= maxRetries) {
              return sendMessage(e, retry + 1, true)
            }

            if (suspendedAccountFeature?.enabled && error?.message === '400301_account_suspended') {
              log.message(`SuspendedUser error: ${error}. Sendbird SDK method: sendUserMessage.`)
              return dispatch(setUserSuspendedType(error.message))
            }
            scrollToBottom(listRef.current)

            if (noSilentBlockFeature?.enabled && MESSAGE_BLOCK_REASONS.includes(error?.message)) {
              return setWarningModal({
                open: true,
                children: (
                  <WarningModalContent
                    type={!blockReasons ? 'default_error' : 'block_reasons'}
                    blockReasonsType={error.message}
                    closeModal={onWarningModalClose}
                    viewTracking={() =>
                      trackModalView({
                        objectId: 'no_silent_block',
                        sptCustom: {
                          role,
                          block_reason: formatBlockReasonTracking(error?.message),
                        },
                      })
                    }
                  />
                ),
              })
            }
            return setChatError({
              error,
              errorType: 'general',
              sentryLog: { description: 'Chat: Sendbird sendUserMessage error' },
            })
          })
      } else {
        setIsSending(false)
      }
    },
    [
      previewImages?.length,
      input,
      uploadImages,
      selectedChannel,
      getVerificationToken,
      formatSingleMessage,
      isFirstMessage,
      sessionStorageKey,
      mcSendEmailCopy,
      setChatError,
      blockReasons,
      onWarningModalClose,
      warningModal,
      role,
      noSilentBlockFeature?.enabled,
      suspendedAccountFeature?.enabled,
      dispatch,
    ],
  )

  const validateMessage = useCallback(
    (e) => {
      const notOnlyWhiteSpace = input.replace(/\s/g, '').length

      if ((listRef.current && input && notOnlyWhiteSpace) || previewImages) {
        if (checkExternalContacts(input) && uriModeration) {
          setWarningModal({
            open: true,
            children: (
              <WarningModalContent
                type="send_message_chat"
                sendMessage={sendMessage}
                closeModal={onWarningModalClose}
                selectedChannelUrl={selectedChannel.url}
                viewTracking={() => trackModalView({ objectId: 'safety_tips' })}
              />
            ),
          })
          return
        }
        sendMessage(e)
      }
    },
    [input, previewImages, uriModeration, sendMessage, onWarningModalClose, selectedChannel.url],
  )

  const handleMoreActionClick = useCallback(() => {
    setShowActionDropDown(true)
  }, [])

  const blockRecipientUser = useCallback(() => {
    sb.blockUserWithUserId(recipient.userId)
      .then((user) => {
        trackingUserBlocksUser(selectedChannel, role)
        setRecipientIsBlocked(true)
      })
      .catch((error) => {
        setChatError({
          error,
          errorType: 'general',
          sentryLog: { description: 'Chat: Sendbird blockUserWithUserId error' },
        })
      })
      .finally(() => {
        setShowActionDropDown(false)
      })
  }, [recipient?.userId, role, sb, selectedChannel, setChatError])

  const unBlockRecipientUser = useCallback(() => {
    sb.unblockUserWithUserId(recipient?.userId)
      .then((user) => {
        trackingUserUnBlocksUser(selectedChannel, role)
        setRecipientIsBlocked(false)
      })
      .catch((error) => {
        setChatError({
          error,
          errorType: 'general',
          sentryLog: { description: 'Chat: Sendbird unblockUserWithUserId error' },
        })
      })
      .finally(() => {
        setShowActionDropDown(false)
      })
  }, [recipient?.userId, role, sb, selectedChannel, setChatError])

  const handleBlockRecipientUser = useCallback(
    (action) => {
      if (action === 'block') {
        blockRecipientUser()
      } else {
        unBlockRecipientUser()
      }
    },
    [blockRecipientUser, unBlockRecipientUser],
  )
  const handleRemoveModal = useCallback(() => {
    setShowRemoveChannelModal(!showRemoveChannelModal)
  }, [showRemoveChannelModal])

  const handleRemoveChannel = useCallback(() => {
    selectedChannel
      .hide(true, true)
      .then((response) => {
        router.push('/meddelanden')
        dispatch(setRemovedChannelUrl(selectedChannel.url))
      })
      .catch((error) => {
        setChatError({
          error,
          errorType: 'general',
          sentryLog: { description: 'Chat: Sendbird remove/hide channel error' },
        })
      })
      .finally(() => {
        setShowRemoveChannelModal(false)
      })
  }, [selectedChannel, router, dispatch, setChatError])

  const handleUploadRefs = useCallback((e, type) => {
    e.preventDefault()
    if (type === 'file') {
      fileUploaderRef.current.click(e)
    }
    if (type === 'image') {
      imageUploaderRef.current.click(e)
    }
  }, [])

  const handleFileUpload = useCallback(
    async (type, e) => {
      const { target } = e
      const { successed, failed } = await validateFiles(type, target, selectedChannel)
      if (failed.length) {
        setChatError(formatErrorFileUploading(failed))
      }

      if (successed.length) {
        for (const file of successed) {
          if (file.generalType === 'image') {
            setPreviewImages((prevImages) => [file, ...prevImages])
          }

          const sendFile = async (retry = 0, forceFetch = false) => {
            const maxRetries = 3

            if (file.generalType === 'file') {
              selectedChannel
                .sendFileMessage(getFileParams(file, await getVerificationToken(forceFetch)))
                .onSucceeded((fileMessage) => {
                  setMessages((prevMessages) => [...prevMessages, fileMessage])
                  scrollToBottom(listRef.current)
                })
                .onFailed((error) => {
                  if (
                    suspendedAccountFeature?.enabled &&
                    error?.message === '400301_account_suspended'
                  ) {
                    log.message(
                      `SuspendedUser error: ${error}. Sendbird SDK method: sendFileMessage -> uploadFiles.`,
                    )
                    return dispatch(setUserSuspendedType(error.message))
                  }

                  if (JWT_ERROR_TYPES.includes(error?.message) && retry <= maxRetries) {
                    return sendFile(retry + 1, true)
                  }

                  trackingUserSendMessageError({
                    selectedChannel,
                    error: error.data,
                    type: 'file',
                  })
                })
            }
          }
          await sendFile()
        }
      }
    },
    [
      selectedChannel,
      setChatError,
      getVerificationToken,
      suspendedAccountFeature?.enabled,
      dispatch,
    ],
  )

  const setRecipientInfo = useCallback(() => {
    const receiver = selectedChannel?.members?.find((member) => member.userId !== currentUserId)
    if (receiver && receiver.isBlockedByMe) {
      setRecipientIsBlocked(true)
    } else {
      setRecipientIsBlocked(false)
    }
    const { ad } = selectedChannel && validateChannel(selectedChannel)
    /* If the ad belongs to the recipient and that person has used an other name when creating the ad on Blocket
    we want to display that name in the sidebar instead of the user nickname. */
    const usersOwnAd = ad?.sbUserId !== receiver?.userId
    const userRole = usersOwnAd ? 'seller' : 'buyer'
    setRecipientName((!usersOwnAd && ad?.advertiserName) || receiver?.nickname)
    setRecipient(receiver)
    setRole(userRole)
  }, [currentUserId, selectedChannel])

  const fetchTip = useCallback(async () => {
    try {
      const adId = JSON.parse(selectedChannel.data).ad.id
      const response = await dispatch(api.fetch(`messaging/v2/tip?ad_id=${adId}`))
      // if there is no tip we receive '204 NO CONTENT' from backend
      if (response.status === 200) {
        setTip(response)
      } else {
        setTip(null)
      }
    } catch (error) {
      setTip(null)
      if (error.response?.status !== 404 || !error.response) {
        log.message(`Chat: Could not call messaging, ${error}`)
      }
    }
  }, [dispatch, selectedChannel])

  const fetchExtensions = useCallback(async () => {
    const transactionEndpoint = `messaging-sendbird-communication/v1/channels/${selectedChannel.url}/transaction`
    const extensionEndpoint = `messaging-be/v1/sb/channels/${selectedChannel.url}/extensions`
    try {
      const transactionResponse = await dispatch(api.fetch(transactionEndpoint))

      if (transactionResponse?.ok) {
        setExtensionData(transactionResponse)
      }
    } catch (transactionError) {
      const { content, response } = transactionError

      if (
        suspendedAccountFeature?.enabled &&
        response?.status === 403 &&
        content?.error === '400301_account_suspended'
      ) {
        log.message(`SuspendedUser error: ${error}. Endpoint: ${transactionEndpoint}`)
        return dispatch(setUserSuspendedType(content.error))
      }

      try {
        // if we get an error from '/transaction', we should fallback to '/extensions'
        const extensionResponse = await dispatch(api.fetch(extensionEndpoint))

        if (extensionResponse?.ok) {
          setExtensionData(extensionResponse)
        }
      } catch (extensionError) {
        const { content, response } = extensionError

        if (
          suspendedAccountFeature?.enabled &&
          response?.status === 403 &&
          content?.error === '400301_account_suspended'
        ) {
          log.message(`SuspendedUser error: ${error}. Endpoint: ${transactionEndpoint}`)
          return dispatch(setUserSuspendedType(content.error))
        }

        setExtensionData(null)
      }
    }
  }, [selectedChannel.url, suspendedAccountFeature?.enabled])

  const updateChannel = useCallback(async () => {
    if (selectedChannel?.custom) return
    const timestamp = selectedChannel?.lastMessage?.createdAt

    try {
      const newMessages = await selectedChannel?.getMessagesByTimestamp(timestamp, {
        prevResultSize: 0,
        nextResultSize: 30,
      })
      const formattedMessages = formatMessages(newMessages)
      setNeedChannelUpdate(false)

      // Check if there are new messages before updating the state
      if (newMessages.length > 0) {
        setMessages((prevMessages) => [...prevMessages, ...formattedMessages])
        selectedChannel?.markAsRead() // needed to update the messaging icon in the header
        await selectedChannel?.refresh() // need to update the channel so it knows about new messages
        scrollToBottom(listRef.current)
      }
    } catch (error) {
      setChatError({
        error,
        errorType: 'general',
        sentryLog: { description: 'Chat: Sendbird updateChannel error' },
      })
    }
  }, [selectedChannel])

  useEffect(async () => {
    const fetchProfileData = async () => {
      try {
        const response = await dispatch(
          api.fetch(`profile-be/v1/public-profiles/${recipientBlocketAccountId}`),
        )
        setProfileData(convertBadgesToJsx(response.textOrJson))
      } catch (error) {
        log.message('fetchAdvertiserData: Error while fetching', error)
      }
    }

    if (recipientBlocketAccountId && !isMobile) {
      await fetchProfileData()
    }
  }, [isMobile, recipientBlocketAccountId])

  // set recipientInfo, set prevMsgListQuery, fetch tip
  useEffect(async () => {
    setPrevMsgListQuery()
    setRecipientInfo()
    await fetchTip()
  }, [])

  // init event handlers
  useEffect(() => {
    if (selectedChannel?.custom) return

    addChannelHandler()
    return () => {
      sb.groupChannel.removeGroupChannelHandler(selectedChannel.url)
    }
  }, [selectedChannel.url, sb, selectedChannel?.custom])

  // fetch (query) initial messages
  useEffect(async () => {
    if (!messageListQuery?.isLoading && queryState.page === 0) {
      await loadMessages()
    }
  }, [queryState, messages, messageListQuery])

  // Fetch extensionData (BlocketBetalning/BlocketPaketet)
  useEffect(() => {
    if (selectedChannel?.url && updateExtensionData) {
      fetchExtensions().then(() => {
        setUpdateExtensionData(false)
        if (listRef.current) {
          scrollToBottom(listRef.current)
        }
      })
    }
  }, [selectedChannel?.url])

  // get sessionStorage for input field
  useEffect(() => {
    const sessionStorageValue = sessionStorageGetItem(sessionStorageKey)
    if (sessionStorageValue) {
      setInput(sessionStorageValue)
    }
    // we need to make the cleanup here to make it work when changing tab and then going back.
    // if we do the cleanup on selectedChannel as above, that one will run after this one..
    return () => {
      setInput('')
    }
  }, [sessionStorageKey])

  // handles scroll on uploading file/image
  useEffect(() => {
    if (uploadingInChannelUrl === selectedChannel.url && !messageListQuery?.isLoading) {
      scrollToBottom(listRef.current)
    }
  }, [uploadingInChannelUrl, selectedChannel, messageListQuery?.isLoading])

  // scroll to bottom on first message load
  useEffect(() => {
    if (listRef.current && queryState && queryState.page === 1) {
      scrollToBottom(listRef.current)
    }
  }, [queryState])

  // scroll to saved pos when savedScrollPos is set
  useEffect(() => {
    if (listRef.current && savedScrollPos && queryState.page > 1) {
      scrollToSavedPos(listRef.current, savedScrollPos)
    }
  }, [savedScrollPos, queryState])

  // only mark as read if there is messages
  useEffect(() => {
    if (connected && selectedChannel?.unreadMessageCount) {
      selectedChannel.markAsRead()
    }
  }, [selectedChannel, selectedChannel.unreadMessageCount, connected])

  // handles messages on loaded channel
  useEffect(() => {
    if (selectedChannel?.custom) return

    if (selectedChannel.getUnreadMemberCount(selectedChannel.lastMessage) <= 0) {
      setRecipientReadLastMessage(true)
    } else {
      setRecipientReadLastMessage(false)
    }
  }, [messages, selectedChannel])

  // Tracking load functions
  useEffect(() => {
    if (selectedChannel?.url && !needsVerification) {
      userViewsChatConversation({ selectedChannel })
    }
  }, [needsVerification, selectedChannel])

  // Tracking tip (3dparty)
  useEffect(() => {
    if (tip) {
      tracking3dPartyAdLoaded({ selectedChannel, tip })
    }
  }, [tip, selectedChannel])

  // if user goes offline or disconnects, we need to update the channel and re-fetch extension data
  useEffect(() => {
    if (!userOnline || !connected) {
      setNeedChannelUpdate(true)
      setUpdateExtensionData(true)
    }
  }, [userOnline, selectedChannel, connected])

  // update channel when going from offline to online, messages received during the time the user has been offline
  useEffect(async () => {
    if (needChannelUpdate && sb && userOnline && connected) {
      setNeedChannelUpdate(false)
      setUpdateExtensionData(false)
      await updateChannel()
    }
  }, [selectedChannel, needChannelUpdate, sb, userOnline, connected])

  return (
    <Container data-testid="channel-view" innerHeight={isMobile && innerHeight}>
      {showActionDropDown && (
        <ActionDropDown
          onClickBlock={handleBlockRecipientUser}
          onClickRemove={handleRemoveModal}
          showReportUserModal={() => {
            setShowActionDropDown(false)
            setReportUserModal({ ...reportUserModal, open: true })
          }}
          recipientIsBlocked={recipientIsBlocked}
          modalRef={modalRef}
        />
      )}
      {showRemoveChannelModal && (
        <RemoveChannelModal
          open={showRemoveChannelModal}
          handleRemoveModal={handleRemoveModal}
          handleRemoveChannel={handleRemoveChannel}
        />
      )}
      {reportUserModal.open && (
        <ReportUserModal
          open={reportUserModal.open}
          label="Report user modal"
          isMobile={isMobile}
          onClose={() => {
            setReportUserModal({ ...reportUserModal, open: false })
          }}
          onChangeInput={(description) => setReportData({ ...reportData, description })}
          renderPage={reportUserModal.activePage}
          formValidation={reportUserModal.formValidation}
          onFocusInput={resetReportUserFormValidation}
          onLinkClick={() => {
            trackCustomerServiceLink(selectedChannel.url)
          }}
          onPillClick={(option) => {
            resetReportUserFormValidation()
            setReportData({ ...reportData, selectedOption: option })
          }}
          onSubmitForm={async (e) => {
            e.preventDefault()

            const invalidReport =
              reportData.selectedOption.reportCategory === 'harassing' &&
              reportData.description.replace(/\s/g, '').length < 1

            if (invalidReport) {
              setReportUserModal({
                ...reportUserModal,
                formValidation: {
                  ...reportUserModal.formValidation,
                  status: 'error',
                  text: 'Du måste lämna en beskrivning',
                },
              })
              trackReportUserInvalidDescription()
              return
            }

            const report = await reportUser({
              selectedChannel,
              offendingUser: reportData.offendingUser,
              reportCategory: reportData.selectedOption.reportCategory,
              description: reportData.description,
            })

            if (report === 'failed') {
              setReportUserModal({
                ...reportUserModal,
                formValidation: {
                  ...reportUserModal.formValidation,
                  status: 'error',
                  text: 'Något gick fel. Försök igen senare!',
                },
              })
              return
            }
            setReportUserModal({ ...reportUserModal, activePage: 'sent' })
            blockRecipientUser()
          }}
          viewTracking={() => {
            const trackingData = { objectId: 'report_user', intent: 'Report' }
            if (reportUserModal.activePage === 'form') {
              trackModalView(trackingData)
            } else {
              trackModalView({
                ...trackingData,
                sptCustom: {
                  report_category: `chat_report_category_${reportData.selectedOption.trackingCategory}`,
                },
              })
            }
          }}
        />
      )}

      {warningModal.open && (
        <WarningModal
          open={warningModal.open}
          onClose={() => {
            trackingWarningModalAction(warningModalType, selectedChannel.url, 'close')
            onWarningModalClose()
          }}
          label="Warning modal"
        >
          {warningModal.children}
        </WarningModal>
      )}

      <ChannelAdInfo
        isMobile={isMobile}
        selectedChannel={selectedChannel}
        onActionBtnClick={handleMoreActionClick}
        recipient={recipient}
        recipientName={recipientName}
        extensionData={extensionData}
        isAdReplyView={isAdReplyView}
        overlayActive={needsVerification}
        profileData={profileData}
        recipientBlocketAccountId={recipientBlocketAccountId}
        recipientVerified={recipientIsBankIdVerified}
      />
      <ChannelView
        isUploading={uploadingInChannelUrl === selectedChannel.url}
        isMobile={isMobile}
        messages={messages}
        listForwardRef={listRef}
        currentUserId={currentUserId}
        typing={typing}
        recipientReadLastMessage={recipientReadLastMessage}
        selectedChannel={selectedChannel}
        tip={tip}
        paginateMessages={paginateMessages}
        messageListQuery={messageListQuery}
        loadingMsgs={messageListQuery?.isLoading}
        recipientName={recipientName}
        isAdReplyView={isAdReplyView}
        previewImages={previewImages}
        removePreviewImage={removePreviewImage}
        overlayActive={needsVerification}
        advertiserType={advertiserType}
      />
      {Boolean(previewImages?.length) && (
        <PreviewImages
          previewImages={previewImages}
          isUploading={uploadingInChannelUrl === selectedChannel.url}
          removePreviewImage={removePreviewImage}
          onImageClick={({ activeIndex }) =>
            dispatch(setCarouselImages({ images: previewImages, activeIndex }))
          }
        />
      )}
      {needsVerification ? (
        <VerificationPrompt type={type} isMobile={isMobile} selectedChannel={selectedChannel} />
      ) : recipientIsBlocked ? (
        <ChannelPrompt promptType="blocked_user" onClick={unBlockRecipientUser} />
      ) : selectedChannel?.custom?.usersOwnAd ? (
        <ChannelPrompt promptType="users_own_ad" />
      ) : (
        <InputField
          input={input}
          onUploadFileChange={handleFileUpload}
          validateMessage={validateMessage}
          handleUploadRefs={handleUploadRefs}
          fileUploaderForwardRef={fileUploaderRef}
          imageUploaderForwardRef={imageUploaderRef}
          textInputForwardRef={textInputRef}
          selectedChannel={selectedChannel}
          setInput={setInput}
          sessionStorageKey={sessionStorageKey}
          previewImages={previewImages}
          isMobile={isMobile}
          isSending={isSending}
        />
      )}
    </Container>
  )
}

LoadedChannel.propTypes = {
  // State if current device is a mobile.
  isMobile: PropTypes.bool,
  // Current user Sendbird ID (user_id: https://docs.sendbird.com/platform/user#_4_resource_representation).
  currentUserId: PropTypes.string.isRequired,
  // Current user selected channel (https://docs.sendbird.com/platform/group_channel#_4_resource_representation).
  selectedChannel: PropTypes.object,
  // Selected channel id.
  pathnameId: PropTypes.string,
  // If rendered in AdReplyView.
  isAdReplyView: PropTypes.bool,
}

export default React.memo(LoadedChannel)
